import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronsDownUp, ChevronsUpDown, Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { COLUMN_LABEL_KEY, availableColumns, COLUMN_WIDTH_CLASS, NAME_WIDTH_CLASS, type ColumnId } from '../lib/columns'

interface Props {
  columns: ColumnId[]
  hasInvestorContract: boolean
  visibility: Record<ColumnId, boolean>
  onToggleColumn: (id: ColumnId) => void
  onCollapseAll: () => void
  onExpandAll: () => void
}

/** Sticky on both axes: the row sticks to the top, its first cell to the left. The column menu follows the Schedule grid (PR #54). */
const BudgetGridHeader: React.FC<Props> = ({ columns, hasInvestorContract, visibility, onToggleColumn, onCollapseAll, onExpandAll }) => {
  const { t } = useTranslation()
  return (
    <div role="row" className="sticky top-0 z-30 flex h-10 min-w-max border-b bg-muted text-xs font-medium text-muted-foreground">
      <div role="columnheader" className={cn('sticky left-0 z-40 flex shrink-0 items-center gap-1 border-r bg-muted px-2', NAME_WIDTH_CLASS)}>
        <Button variant="ghost" className="h-7 w-7 p-0" onClick={onCollapseAll} aria-label={t('CostControl_collapse_all')} title={t('CostControl_collapse_all')}>
          <ChevronsDownUp className="h-4 w-4" />
        </Button>
        <Button variant="ghost" className="h-7 w-7 p-0" onClick={onExpandAll} aria-label={t('CostControl_expand_all')} title={t('CostControl_expand_all')}>
          <ChevronsUpDown className="h-4 w-4" />
        </Button>
        <span className="flex-1 truncate">{t('CostControl_col_name')}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-7 w-7 p-0" aria-label={t('CostControl_columns')} title={t('CostControl_columns')}>
              <Settings2 className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('CostControl_columns')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {availableColumns(hasInvestorContract).map((id) => (
              <DropdownMenuCheckboxItem key={id} checked={!!visibility[id]} onCheckedChange={() => onToggleColumn(id)} onSelect={(e) => e.preventDefault()}>
                {t(COLUMN_LABEL_KEY[id])}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {columns.map((id) => (
        <div key={id} role="columnheader" title={t(COLUMN_LABEL_KEY[id])} className={cn('flex shrink-0 items-center px-2 leading-tight', COLUMN_WIDTH_CLASS[id], id !== 'code' && 'justify-end text-right')}>
          <span className="line-clamp-2">{t(COLUMN_LABEL_KEY[id])}</span>
        </div>
      ))}
    </div>
  )
}
export default BudgetGridHeader
